import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RankPatentBadge } from '@/components/ranks/RankPatentBadge';
import { formatRankMetric } from '@/lib/ranks/formatRankMetric';
import { joinRankMetricUnit, rankMetricUnitKey } from '@/lib/ranks/rankMetricUnit';
import { rankTierRequirement } from '@/lib/ranks/rankTierRequirement';
import { trackLadderRows } from '@/lib/ranks/trackLadderRows';
import { cn } from '@/lib/utils';
import type { ClipperRankTrackId } from '@/generated/graphql';

export type RankTrackLadderTrack = {
  id: ClipperRankTrackId;
  current_tier_id?: string | null;
  unlocked_tier_ids: string[];
};

export function RankTrackLadder({ track }: { track: RankTrackLadderTrack }) {
  const { t, i18n } = useTranslation();
  const labelId = useId();
  const unitKey = rankMetricUnitKey(track.id);
  const unit = unitKey ? t(unitKey) : null;
  const rows = trackLadderRows({
    trackId: track.id,
    currentTierId: track.current_tier_id,
    unlockedTierIds: track.unlocked_tier_ids,
  });

  const thresholdFor = (tierId: string): string | null => {
    const requirement = rankTierRequirement(tierId);
    if (!requirement) return null;
    if (requirement.kind === 'affiliate_sale') return t('ranks.ladder.affiliateBronze');
    if (requirement.kind === 'season_exact' || requirement.kind === 'season_range') return null;
    return joinRankMetricUnit(
      formatRankMetric({ trackId: track.id, metric: requirement.metric, language: i18n.language }),
      unit,
    );
  };

  return (
    <ul
      data-testid="clipper-ranks-track-ladder"
      aria-labelledby={labelId}
      className="space-y-0"
    >
      <li id={labelId} className="sr-only">
        {t('ranks.ladder.show')}
      </li>
      {rows.map((row, index) => {
        const threshold = thresholdFor(row.tierId);
        const next = rows[index + 1];
        return (
          <li
            key={row.tierId}
            data-tier-id={row.tierId}
            data-state={row.current ? 'current' : row.earned ? 'earned' : 'locked'}
            className={cn(
              'relative flex items-center gap-3 rounded-lg px-2 py-2',
              row.current && 'bg-primary/10 ring-1 ring-primary/40',
            )}
          >
            {/* Rail from this badge's centre down to the next one's, so the
                earned stretch reads as one continuous climb. */}
            {next ? (
              <span
                aria-hidden="true"
                className={cn(
                  'absolute left-[27px] top-1/2 h-full w-0.5',
                  next.earned ? 'bg-primary/60' : 'bg-border',
                )}
              />
            ) : null}
            <RankPatentBadge
              tierId={row.tierId}
              size="md"
              showLabel={false}
              earned={row.earned}
              interactive={false}
              className={cn('relative z-10', !row.earned && 'opacity-60')}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span
                  className={cn(
                    'text-sm font-medium',
                    row.earned ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {t(`ranks.tiers.${row.tierId}`)}
                </span>
                {row.current ? (
                  <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">
                    {t('ranks.ladder.current')}
                  </Badge>
                ) : null}
              </div>
              {threshold ? (
                <p className="text-xs tabular-nums text-muted-foreground">{threshold}</p>
              ) : null}
            </div>
            {row.earned ? (
              <Check
                aria-label={t('ranks.ladder.earned')}
                className="h-4 w-4 shrink-0 text-primary"
              />
            ) : (
              <Lock
                aria-label={t('ranks.ladder.locked')}
                className="h-4 w-4 shrink-0 text-muted-foreground/50"
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
